import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { EditOutlined as Edit, VisibilityOutlined as Visibility, ArrowUpwardOutlined as ArrowUpward, ArrowDownwardOutlined as ArrowDownward } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchAllUsers, updateUserRole } from '../store/slices/adminSlice';
import Pagination from '../components/common/Pagination';
import axios from 'axios';
import backendUrl from '../store/config';
import { safeFormatDate } from '../utils/dateUtils';

const UserManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, loading, error } = useAppSelector((state: any) => state.admin);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const authToken = useAppSelector((state: any) => state.auth.token);
  const [selectedUser, setSelectedUser] = React.useState<any>(null);
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [newRole, setNewRole] = React.useState<'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user'>('user');
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = React.useState<any>(null);
  const [profileLoading, setProfileLoading] = React.useState(false);
  
  // User Management sorting states
  const [userSortField, setUserSortField] = React.useState<string>('last_login');
  const [userSortDirection, setUserSortDirection] = React.useState<'asc' | 'desc'>('desc');
  
  // Pagination states
  const [userCurrentPage, setUserCurrentPage] = React.useState(1);
  const [userItemsPerPage, setUserItemsPerPage] = React.useState(10);

  // Sorting and pagination logic for users
  const sortedUsers = React.useMemo(() => {
    if (!users || users.length === 0) return [];
    
    return [...users].sort((a: any, b: any) => {
      let aValue: any, bValue: any;
      
      switch (userSortField) {
        case 'username':
          aValue = a.username || '';
          bValue = b.username || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'role':
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        case 'status':
          aValue = a.is_active ? 'Active' : 'Inactive';
          bValue = b.is_active ? 'Active' : 'Inactive';
          break;
        case 'summary_count':
          aValue = a.summary_count || 0;
          bValue = b.summary_count || 0;
          break;
        case 'query_count':
          aValue = a.query_count || 0;
          bValue = b.query_count || 0;
          break;
        case 'last_login':
        default:
          aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
          bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return userSortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return userSortDirection === 'asc' ? comparison : -comparison;
      }
    });
  }, [users, userSortField, userSortDirection]);

  const paginatedUsers = React.useMemo(() => {
    const startIndex = (userCurrentPage - 1) * userItemsPerPage;
    const endIndex = startIndex + userItemsPerPage;
    return sortedUsers.slice(startIndex, endIndex);
  }, [sortedUsers, userCurrentPage, userItemsPerPage]);

  const userTotalPages = Math.ceil(sortedUsers.length / userItemsPerPage);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  const handleRoleChange = async (userId: string, role: 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user') => {
    try {
      await dispatch(updateUserRole({ userId, role })).unwrap();
      // Auto-refresh the users list to show updated role immediately
      dispatch(fetchAllUsers());
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
    setRoleDialogOpen(false);
    setSelectedUser(null);
  };

  const openRoleDialog = (user: any) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  const openProfileDialog = async (userId: string) => {
    setProfileDialogOpen(true);
    setProfileLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/users/${userId}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      setSelectedUserDetail(res.data);
    } catch (e) {
      setSelectedUserDetail(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUserSort = (field: string) => {
    if (userSortField === field) {
      setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDirection('desc');
    }
  };

  const getUserSortIcon = (field: string) => {
    if (userSortField !== field) return null;
    return userSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  return (
    <Fade in timeout={600}>
      <Container
        maxWidth="xl"
        sx={{
          mt: isMobile ? 2 : 4,
          mb: isMobile ? 2 : 4,
          px: isMobile ? 1 : 3,
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 500, 
            mb: isMobile ? 2 : 4,
            fontSize: isMobile ? '1.5rem' : undefined
          }}
        >
          User Management
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* User Management */}
      <Fade in timeout={1000}>
        <Card>
        <CardContent>
          {loading ? (
            <LinearProgress />
          ) : (
            <>
              <TableContainer 
                component={Paper} 
                variant="outlined"
                sx={{
                  overflowX: 'auto',
                  '& .MuiTable-root': {
                    minWidth: isMobile ? 800 : 'auto'
                  }
                }}
              >
                <Table size={isMobile ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('username')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          User
                          {getUserSortIcon('username')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('email')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Email
                          {getUserSortIcon('email')}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('role')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Primary Role
                          {getUserSortIcon('role')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        All App Roles
                      </TableCell>
                      <TableCell>
                        Azure Groups
                      </TableCell>
                      <TableCell
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('status')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Status
                          {getUserSortIcon('status')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('summary_count')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Summaries
                          {getUserSortIcon('summary_count')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('query_count')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Queries
                          {getUserSortIcon('query_count')}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleUserSort('last_login')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Last Login
                          {getUserSortIcon('last_login')}
                        </Box>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user: any) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: user.role === 'admin' ? 'primary.main' : 'grey.500' }}>
                              {user.username ? user.username.charAt(0).toUpperCase() : '?'}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 400 }}>
                              {user.username || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role || 'user'}
                            color={
                              user.role === 'superadmin' ? 'error' :
                              user.role === 'admin' ? 'primary' :
                              user.role === 'anis-admin' ? 'warning' :
                              'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 250 }}>
                            {user.roles && user.roles.length > 0 ? (
                              user.roles.map((role: string) => (
                                <Chip
                                  key={role}
                                  label={role}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    role === 'superadmin' ? 'error' :
                                    role === 'admin' ? 'primary' :
                                    role === 'anis-admin' ? 'warning' :
                                    'default'
                                  }
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              ))
                            ) : (
                              <Chip label={user.role || 'user'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem', maxWidth: 300, maxHeight: 60, overflow: 'auto' }}>
                            {user.azure_roles && user.azure_roles.length > 0
                              ? user.azure_roles.filter((r: string) =>
                                  r.includes('AIHub') || r.includes('Anis') || r.includes('aihub')
                                ).join(', ') || '(no AIHub groups)'
                              : '(none)'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{user.summary_count}</TableCell>
                        <TableCell>{user.query_count}</TableCell>
                        <TableCell>
                          {safeFormatDate(user.last_login, 'MMM dd, yyyy', 'Never')}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Profile">
                              <IconButton size="small" onClick={() => openProfileDialog(user.id)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Change Role">
                              <IconButton
                                size="small"
                                onClick={() => openRoleDialog(user)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {sortedUsers && sortedUsers.length > 0 && (
                <Pagination
                  currentPage={userCurrentPage}
                  totalPages={userTotalPages}
                  totalItems={sortedUsers.length}
                  itemsPerPage={userItemsPerPage}
                  onPageChange={setUserCurrentPage}
                  onItemsPerPageChange={setUserItemsPerPage}
                />
              )}
            </>
          )}
        </CardContent>
        </Card>
      </Fade>

      {/* Role Management Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Change role for user: <strong>{selectedUser?.username}</strong>
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              label="Role"
              onChange={(e) => setNewRole(e.target.value as 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user')}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="anis-user">ANIS User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="anis-admin">ANIS Admin</MenuItem>
              <MenuItem value="superadmin">Super Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleRoleChange(selectedUser?.id, newRole)}
            variant="contained"
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Profile</DialogTitle>
        <DialogContent>
          {profileLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : selectedUserDetail ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Username</Typography>
                <Typography variant="body1">{selectedUserDetail.username}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedUserDetail.email}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">Role</Typography>
                <Chip label={selectedUserDetail.role} color={selectedUserDetail.role === 'admin' ? 'primary' : 'default'} size="small" />
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip label={selectedUserDetail.is_active ? 'Active' : 'Inactive'} color={selectedUserDetail.is_active ? 'success' : 'error'} size="small" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Member Since</Typography>
                <Typography variant="body2">{safeFormatDate(selectedUserDetail.created_at, 'MMM dd, yyyy', '')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last Login</Typography>
                <Typography variant="body2">{safeFormatDate(selectedUserDetail.last_login, 'MMM dd, yyyy', 'Never')}</Typography>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">Failed to load user details.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      </Container>
    </Fade>
  );
};

export default UserManagement;
